import { CalendarEvent } from "@schedule-x/calendar";
import { WeatherIcon, WeatherType } from "@/components/ui/weather-icons";
import { Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { usePlanningCounts } from "@/hooks/usePlanningCountsCore";

export interface TimeGridEventData extends CalendarEvent {
  // Données supplémentaires pour notre composant personnalisé
  period?: "matin" | "midi" | "apres-midi" | "journee";
  currentChildren?: number;
  maxChildren?: number;
  weather?: {
    type: WeatherType;
    temperature: number;
  };
  // Données pour le bouton conditionnel (ex: appel)
  actionButton?: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    variant?: "default" | "outline" | "secondary" | "ghost";
    className?: string;
  };
  backgroundColor?: string;
}

interface CustomTimeGridEventProps {
  calendarEvent: TimeGridEventData;
  onSelectPeriod?: (calendarEvent: TimeGridEventData) => void;
}

// Fonction pour obtenir le libellé français d'une période
const getPeriodLabel = (period: "matin" | "midi" | "apres-midi" | "journee", isShort = false): string => {
  switch (period) {
    case "matin":
      return "Matinée";
    case "midi":
      return "Midi";
    case "apres-midi":
      return isShort ? "Aprèm" : "Après-midi";
    case "journee":
      return "Journée";
    default:
      return "";
  }
};

export function CustomTimeGridEvent({ calendarEvent, onSelectPeriod }: CustomTimeGridEventProps) {
  const { title, period, currentChildren, maxChildren, weather, actionButton, backgroundColor } = calendarEvent;
  const containerRef = useRef<HTMLDivElement>(null);
  const [displayMode, setDisplayMode] = useState<"normal" | "compact" | "minimal">("normal");
  const countsContext = usePlanningCounts();

  // Fallback robuste: si les champs currentChildren / maxChildren ne sont pas fournis
  // (ex: lib qui clone les events sans props custom), on tente de parser à partir du title "(x/y)".
  // Calcul direct (léger) pour éviter les problèmes de dépendances avec le compilateur React 19
  let displayedCurrent = 0;
  let displayedMax = 0;
  // 1) Contexte
  if (countsContext && calendarEvent?.id) {
    const fromCtx = countsContext[String(calendarEvent.id)];
    if (fromCtx) {
      displayedCurrent = fromCtx.current;
      displayedMax = fromCtx.max;
    }
  }
  // 2) Props si pas de contexte
  if (displayedMax === 0 && typeof currentChildren === "number" && typeof maxChildren === "number") {
    displayedCurrent = currentChildren;
    displayedMax = maxChildren;
  }
  // 3) Parsing du titre en dernier recours
  if (displayedMax === 0 && typeof title === "string") {
    const match = title.match(/\((\d+)\/(\d+)\)/);
    if (match) {
      const [, c, m] = match;
      const parsedC = Number.parseInt(c, 10);
      const parsedM = Number.parseInt(m, 10);
      if (Number.isFinite(parsedC) && Number.isFinite(parsedM)) {
        displayedCurrent = parsedC;
        displayedMax = parsedM;
      }
    }
  }

  // Déterminer le mode d'affichage selon la hauteur
  useEffect(() => {
    const updateDisplayMode = () => {
      if (containerRef.current) {
        const height = containerRef.current.offsetHeight;
        if (height < 90) {
          setDisplayMode("minimal");
        } else if (height < 110) {
          setDisplayMode("compact");
        } else {
          setDisplayMode("normal");
        }
      }
    };

    updateDisplayMode();
    const resizeObserver = new ResizeObserver(updateDisplayMode);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  // Déterminer la couleur de fond
  const bgColor = backgroundColor || "bg-blue-100";

  // Formatage de l'horaire
  const startTime = (calendarEvent.start as Temporal.ZonedDateTime)?.toLocaleString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const endTime = (calendarEvent.end as Temporal.ZonedDateTime)?.toLocaleString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const timeRange = startTime && endTime ? `${startTime} - ${endTime}` : "";

  // Calculer la durée en minutes
  const getDurationInMinutes = (): number => {
    if (!calendarEvent.start || !calendarEvent.end) return 0;
    const start = calendarEvent.start as Temporal.ZonedDateTime;
    const end = calendarEvent.end as Temporal.ZonedDateTime;
    return start.until(end, { largestUnit: "minutes" }).minutes;
  };

  const durationMinutes = getDurationInMinutes();
  const hasActionButton = !!actionButton;

  // Déterminer si on doit utiliser un affichage compact basé sur la durée
  const shouldUseCompactDuration = () => {
    const threshold = hasActionButton ? 90 : 60; // 1h30 avec bouton, 1h sans bouton
    return durationMinutes > 0 && durationMinutes <= threshold;
  };

  const isCompactDuration = shouldUseCompactDuration();

  // Classes dynamiques selon le mode
  const getTextClasses = () => {
    switch (displayMode) {
      case "minimal":
        return {
          title: "text-xs font-semibold text-white",
          time: "text-[10px] text-white",
          info: "text-xs text-white",
          button: "h-4 w-auto px-8 pb-2 text-xs rounded-sm !border-0 !shadow-none underline hover:!no-underline !decoration-1",
          icon: "h-2 w-2",
          padding: "p-1 pb-1",
          meteo: "h-4 w-4",
        };
      case "compact":
        return {
          title: "text-sm font-semibold text-white",
          time: "text-xs text-white",
          info: "text-xs text-white",
          button: "h-6 w-32 text-xs !border-2",
          icon: "h-3 w-3",
          padding: "p-1.5 pb-1",
          meteo: "h-4 w-4",
        };
      default:
        return {
          title: "text-sm font-semibold text-white",
          time: "text-xs text-white",
          info: "text-sm text-white",
          button: "h-6 w-32 text-xs !border-2",
          icon: "h-4 w-4",
          padding: "p-2 pb-1",
          meteo: "h-5 w-5",
        };
    }
  };

  const classes = getTextClasses();

  return (
    <div
      ref={containerRef}
      className={`relative flex h-full w-[95%] cursor-pointer flex-col justify-between overflow-hidden rounded-md shadow-sm transition duration-150 ease-out outline-none select-none hover:shadow-md hover:brightness-105 focus-visible:shadow-md focus-visible:brightness-110 ${bgColor} ${classes.padding}`}
      onClick={() => onSelectPeriod?.(calendarEvent)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelectPeriod?.(calendarEvent);
        }
      }}
      style={{
        backgroundColor: backgroundColor,
      }}
    >
      {/* Partie supérieure - Titre et horaire */}
      <div className="flex flex-col">
        {(period || title) && (
          <div className={`leading-tight ${classes.title}`}>
            {/* Si durée courte, afficher titre et heure sur la même ligne */}
            {isCompactDuration && timeRange ? (
              <div className="flex items-center justify-between">
                <span className="truncate">{period ? getPeriodLabel(period, true) : title}</span>
                <span className={`ml-2 flex-shrink-0 ${classes.time}`}>{timeRange}</span>
              </div>
            ) : // Affichage normal
            period ? (
              getPeriodLabel(period)
            ) : (
              title
            )}
          </div>
        )}
        {timeRange && !isCompactDuration && <div className={`mt-0.5 ${classes.time}`}>{timeRange}</div>}
      </div>

      {/* Partie centrale - Bouton conditionnel */}
      {actionButton && (
        <div className="flex items-center justify-center py-1">
          <Button
            variant={actionButton.variant || "default"}
            size="icon"
            onClick={actionButton.onClick}
            className={`${classes.button} !border-white px-2 py-1 text-white hover:!bg-white hover:!text-black ${actionButton.className || ""}`}
          >
            {actionButton.icon && <span className={`mr-1 flex ${classes.icon} items-center justify-center`}>{actionButton.icon}</span>}
            {actionButton.label}
          </Button>
        </div>
      )}

      {/* Partie inférieure - Capacité et météo */}
      <div className="flex items-end justify-between">
        <div className={`flex items-center ${classes.info}`}>
          <Users className={`mr-1 ${classes.icon}`} />
          <span className="font-medium">
            {displayedCurrent}/{displayedMax}
          </span>
        </div>
        {weather && (
          <div className={`flex items-center ${classes.info}`}>
            <span className="mr-1 font-medium text-white">{weather.temperature}°</span>
            <WeatherIcon type={weather.type} className={`${classes.meteo} text-white`} />
          </div>
        )}
      </div>
    </div>
  );
}
